import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const trustBadges = [
  'HIPAA-Compliant',
  'Fully Licensed & Insured',
  'All Major Insurance Accepted',
  'Private Pay Welcome',
];

export default function Footer() {
  return (
    <footer
      className="bg-primary text-primary-foreground border-t border-white/10"
      role="contentinfo"
      aria-label="Site footer"
    >
      {/* Trust Strip */}
      <div className="border-b border-white/10 py-4">
        <div className="max-w-screen-xl mx-auto px-6 md:px-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
          {trustBadges?.map((badge) => (
            <span key={badge} className="flex items-center gap-2 text-xs font-semibold text-white/70 uppercase tracking-wider">
              <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" aria-hidden="true" />
              {badge}
            </span>
          ))}
        </div>
      </div>
      {/* Main Footer */}
      <div className="max-w-screen-xl mx-auto px-6 md:px-10 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">

          {/* Brand Block */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <AppLogo size={40} />
              <span className="font-display font-semibold text-xl text-primary-foreground">
                Kapra Medical Transportation
              </span>
            </div>
            <p className="text-sm text-white/60 leading-relaxed max-w-xs font-light">
              &ldquo;We treat your family like our own.&rdquo;<br />
              Non-emergency medical transport in Northern New Jersey.
            </p>

            {/* NAP Block */}
            <address className="not-italic flex flex-col gap-2 text-sm text-white/70">
              <span className="flex items-start gap-2">
                <Icon name="MapPinIcon" size={16} className="shrink-0 mt-0.5 text-accent" />
                5 Newark Pompton Turnpike, Riverdale, NJ 07457
              </span>
              <a
                href="tel:+15513269573"
                className="flex items-center gap-2 hover:text-accent transition-colors"
                aria-label="Call (551) 326-9573"
              >
                <Icon name="PhoneIcon" size={16} className="shrink-0 text-accent" />
                (551) 326-9573
              </a>
              <a
                href="mailto:info@kapra-ems.com"
                className="flex items-center gap-2 hover:text-accent transition-colors"
                aria-label="Email info@kapra-ems.com"
              >
                <Icon name="EnvelopeIcon" size={16} className="shrink-0 text-accent" />
                info@kapra-ems.com
              </a>
            </address>

            {/* Hours */}
            <div className="text-sm text-white/60">
              <p className="font-semibold text-white/80 text-xs uppercase tracking-wider mb-1">Hours</p>
              <p>Monday – Saturday: 3:00 AM – 8:00 PM</p>
              <p>Sunday: Closed</p>
            </div>

            {/* Since 2013 */}
            <p className="text-xs text-white/40 font-medium">Serving New Jersey since 2013.</p>
          </div>

          {/* Services Column */}
          <div className="lg:col-span-4">
            <p className="text-xs font-bold uppercase tracking-widest text-white/40 mb-4">Services</p>
            <ul className="flex flex-col gap-3">
              {[
                { label: 'Dialysis Transportation', href: '/services/dialysis-transportation' },
                { label: 'Wheelchair Transportation', href: '/services/wheelchair-transportation' },
                { label: 'Stretcher Transportation', href: '/services/stretcher-transportation' },
                { label: 'Medicaid Transportation', href: '/services/medicaid-transportation' },
                { label: 'Hospital Discharge', href: '/services/hospital-discharge' },
                { label: 'Nursing Home Transportation', href: '/services/nursing-home-transportation' },
                { label: 'Assisted Living Transportation', href: '/services/assisted-living-transportation' },
                { label: 'Hospice Transportation', href: '/services/hospice-transportation' },
              ]?.map((item) => (
                <li key={item?.href}>
                  <Link
                    href={item?.href}
                    className="text-sm text-white/65 hover:text-accent transition-colors font-medium"
                  >
                    {item?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links Column */}
          <div className="lg:col-span-3">
            <p className="text-xs font-bold uppercase tracking-widest text-white/40 mb-4">Company</p>
            <ul className="flex flex-col gap-3">
              {[
                { label: 'About Us', href: '/about' },
                { label: 'Careers', href: '/careers' },
                { label: 'Contact', href: '/contact' },
                { label: 'Who Qualifies?', href: '/medical-transportation-eligibility' },
                { label: 'For Facilities', href: '/facilities' },
              ]?.map((item) => (
                <li key={item?.href}>
                  <Link
                    href={item?.href}
                    className="text-sm text-white/65 hover:text-accent transition-colors font-medium"
                  >
                    {item?.label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-8">
              <p className="text-xs font-bold uppercase tracking-widest text-white/40 mb-3">Service Area</p>
              <p className="text-sm text-white/60 leading-relaxed">
                Passaic, Bergen, Sussex, Morris, and Essex counties across Northern New Jersey.
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Bottom Bar */}
      <div className="border-t border-white/10 py-5">
        <div className="max-w-screen-xl mx-auto px-6 md:px-10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/40">
          <span>© 2026 Kapra Medical Transportation. All rights reserved.</span>
          <div className="flex items-center gap-6">
            <Link href="/about" className="hover:text-white/70 transition-colors">About</Link>
            <Link href="/privacy" className="hover:text-white/70 transition-colors">Privacy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}